import { chakra } from "@chakra-ui/react";
import { replacePlaceholders } from "../layout/documentLayout";
import { firstAnswer } from "./answerFormat";
import {
  buildChartRows,
  buildIcdRows,
  buildPocRows,
  canBeChildOfChart,
  ICD_HEADINGS,
} from "./blockFormat";
import { BarTitle, PRINT_MUTED_COLOR, PrintBlock, TitledBlock } from "./printStyles";
import type { QuestionViewProps } from "./types";

// chart, icd / icdChart / sectionedIcdCodes, POC -> the PDF's Table block: grey title bar,
// underlined column headings, muted cells. (icdCodes / finalIcdCodes / medicalDiagnosis print the
// patient's live records: EntityQuestion.tsx.)

/** `.block-inline-title` as a table heading */
const TableHeading = chakra("th", {
  baseStyle: {
    borderBottom: "2px solid",
    fontWeight: 600,
    lineHeight: "25px",
    p: "8px",
    textAlign: "start",
  },
});

const Cell = chakra("td", {
  baseStyle: {
    color: PRINT_MUTED_COLOR,
    p: "8px",
    verticalAlign: "top",
  },
});

/** The PDF's Table block; without `title`, only the table (the host prints the title bar). */
export function PrintTable(props: { headings: string[]; rows: string[][]; title?: string }) {
  return (
    <PrintBlock display="flex" flexDirection="column">
      {props.title !== undefined && <BarTitle>{props.title}</BarTitle>}
      {props.headings.length > 0 && (
        <chakra.div overflowX="auto">
          <chakra.table fontSize="inherit" sx={{ borderCollapse: "collapse" }} width="100%">
            <thead>
              <tr>
                {props.headings.map((heading, index) => (
                  <TableHeading key={index}>{heading}</TableHeading>
                ))}
              </tr>
            </thead>
            <tbody>
              {props.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((value, cellIndex) => (
                    <Cell key={cellIndex}>{value}</Cell>
                  ))}
                </tr>
              ))}
            </tbody>
          </chakra.table>
        </chakra.div>
      )}
    </PrintBlock>
  );
}

export function ChartQuestionView({ answers, block, placeholders }: QuestionViewProps) {
  const columns = (block.question.columns?.[0] ?? []).filter((child) =>
    canBeChildOfChart(child.itemType)
  );

  return (
    <PrintTable
      headings={columns.map((column) => replacePlaceholders(column.label, placeholders))}
      rows={buildChartRows(columns, answers, placeholders)}
      title={replacePlaceholders(block.question.label, placeholders)}
    />
  );
}

export function IcdQuestionView({ block, layoutType, placeholders }: QuestionViewProps) {
  // Like the PDF, codes are listed only when the question has exactly one answer row.
  const hasCodes = block.answers.length === 1;

  return (
    <PrintTable
      headings={hasCodes ? ICD_HEADINGS : []}
      rows={hasCodes ? buildIcdRows(firstAnswer(block.answers), layoutType) : []}
      title={replacePlaceholders(block.question.label, placeholders)}
    />
  );
}

const POC_HEADINGS = ["Service", "Schedule", "Notes"];

export function PocQuestionView({ block }: QuestionViewProps) {
  const rows = buildPocRows(block.question, firstAnswer(block.answers));

  if (rows.length === 0) {
    return (
      <TitledBlock gap="6px" isInline={false} title="Plan of Care">
        <div>No POC items available</div>
      </TitledBlock>
    );
  }

  return <PrintTable headings={POC_HEADINGS} rows={rows} title="Plan of Care (POC)" />;
}
